import { user_outline, office_building, infoToolTip } from '../../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../../common/_custom/Tooltip/Tooltip';
import type { AuditorYearRow, AuditorCommentsCellProps, AuditorPerson } from '../../auditorCommentsDrawer.types';
import {
    ADDITIONAL_AUDITORS_LABEL,
    AUDITOR_COMMENTS_COLUMN_LABELS,
    AUDITOR_TOOLTIP_LABELS,
    FOOTNOTE_LABEL,
    NO_DATA_LABEL,
    QUALIFICATION_LABELS,
} from '../../constants';
import styles from './AuditorCommentsCells.module.scss';

export const AuditorDetails = ({ row, isMobile }: { row: AuditorYearRow; isMobile: boolean }) => {
    if (!row.auditorName && !row.auditorFirmName && !row.additionalAuditors.length) {
        return <span className={styles.NoData}>{NO_DATA_LABEL}</span>;
    }
    const mainAuditor: AuditorPerson = {
        nameOfAuditor: row.auditorName,
        firmName: row.auditorFirmName,
        membershipNum: row.auditorMembershipNum,
        firmRegistrationNumber: row.auditorFirmRegistrationNumber,
        address: row.auditorAddress,
        pan: row.auditorPan,
    };
    return (
        <>
            <AuditorPersonDetails person={mainAuditor} isMobile={isMobile} />
            {row.additionalAuditors.length > 0 && (
                <>
                    <div className={styles.AdditionalAuditorsLabel}>{ADDITIONAL_AUDITORS_LABEL}</div>
                    {row.additionalAuditors.map((person, idx) => (
                        <AuditorPersonDetails key={idx} person={person} isMobile={isMobile} />
                    ))}
                </>
            )}
        </>
    );
};

export const QualificationBadge = ({ hasAdverseRemarks }: { hasAdverseRemarks: boolean | null }) => {
    if (hasAdverseRemarks == null) return null;
    if (hasAdverseRemarks) {
        return <div className={`${styles.QualificationBadge} ${styles.Qualified}`}>{QUALIFICATION_LABELS.QUALIFIED}</div>;
    }
    return <div className={`${styles.QualificationBadge} ${styles.NotQualified}`}>{QUALIFICATION_LABELS.NOT_QUALIFIED}</div>;
};

export const CommentsList = ({ comments }: { comments: string[] }) => {
    if (!comments.length) return null;
    return (
        <ul className={styles.CommentsList}>
            {comments.map((comment, idx) => (
                <li key={idx}>{comment}</li>
            ))}
        </ul>
    );
};

export const AuditorCommentsContent = ({ row, showBadge = true, showFootnotes = true }: AuditorCommentsCellProps) => {
    const showsQualification = showBadge && row.reportHasAdverseRemarks != null;
    const hasComments = row.auditorComments.length > 0 || row.auditorFootnotes.length > 0;

    if (!showsQualification && !hasComments) {
        return <span className={styles.NoData}>{NO_DATA_LABEL}</span>;
    }

    return (
        <>
            {showBadge && <QualificationBadge hasAdverseRemarks={row.reportHasAdverseRemarks} />}
            <CommentsList comments={row.auditorComments} />
            {showFootnotes && <Footnotes footnotes={row.auditorFootnotes} />}
        </>
    );
};

export const DirectorCommentsContent = ({ row, showFootnotes = true }: Omit<AuditorCommentsCellProps, 'showBadge'>) => {
    const hasBadgeOffset = row.reportHasAdverseRemarks != null;
    if (!row.directorComments.length && !row.directorFootnotes.length) {
        return <span className={`${styles.NoData} ${hasBadgeOffset ? styles.BadgeOffset : ''}`}>{NO_DATA_LABEL}</span>;
    }
    return (
        <div className={hasBadgeOffset ? styles.BadgeOffset : undefined}>
            <CommentsList comments={row.directorComments} />
            {showFootnotes && <Footnotes footnotes={row.directorFootnotes} />}
        </div>
    );
};

const AuditorInfoTooltip = ({ person, isMobile }: { person: AuditorPerson; isMobile: boolean }) => {
    if (!hasAuditorExtraDetails(person)) return null;
    return (
        <Tooltip
            content={<AuditorDetailsCard person={person} />}
            direction={isMobile ? 'top' : 'right'}
            usePortal
            focusable
            hideArrow
            tipClassName={styles.AuditorCardTip}
            contentClassName={styles.AuditorCardTooltip}
            fitContent
            edgePadding={isMobile ? 12 : undefined}
            triggerOnClick={isMobile}
        >
            <img
                src={infoToolTip}
                alt={AUDITOR_COMMENTS_COLUMN_LABELS.AUDITOR_DETAILS}
                width="16.81"
                height="16.81"
                className={styles.InfoIcon}
            />
        </Tooltip>
    );
};

const AuditorPersonDetails = ({ person, isMobile }: { person: AuditorPerson; isMobile: boolean }) => {
    const nameClassName = hasAuditorExtraDetails(person) ? styles.TruncatedName : undefined;
    return (
        <>
            {person.nameOfAuditor && (
                <div className={styles.AuditorDetailItem}>
                    <img src={user_outline} alt="" width="18" height="18" className={styles.AuditorNameIcon} />
                    <span className={nameClassName}>{person.nameOfAuditor}</span>
                    <AuditorInfoTooltip person={person} isMobile={isMobile} />
                </div>
            )}
            {person.firmName && (
                <div className={styles.AuditorDetailItem}>
                    <img src={office_building} alt="" width="18.8" height="16" className={styles.AuditorFirmIcon} />
                    <span className={nameClassName}>{person.firmName}</span>
                    <AuditorInfoTooltip person={person} isMobile={isMobile} />
                </div>
            )}
        </>
    );
};

export const Footnotes = ({ footnotes }: { footnotes: string[] }) => (
    <>
        {footnotes.map((footnote, idx) => (
            <p key={idx} className={styles.Footnote}>
                <span className={styles.FootnoteLabel}>{FOOTNOTE_LABEL}</span> {footnote}
            </p>
        ))}
    </>
);

export const AuditorDetailsCard = ({ person }: { person: AuditorPerson }) => {
    const rows: [string, string | null | undefined, boolean][] = [
        [AUDITOR_TOOLTIP_LABELS.AUDITOR_NAME, person.nameOfAuditor, true],
        [AUDITOR_TOOLTIP_LABELS.MEMBERSHIP_NO, person.membershipNum, false],
        [AUDITOR_TOOLTIP_LABELS.FIRM_NAME, person.firmName, true],
        [AUDITOR_TOOLTIP_LABELS.FRN, person.firmRegistrationNumber, false],
        [AUDITOR_TOOLTIP_LABELS.PAN, person.pan, false],
        [AUDITOR_TOOLTIP_LABELS.ADDRESS, person.address, false],
    ];
    return (
        <div className={styles.AuditorCard}>
            <div className={styles.AuditorCardTitle}>{AUDITOR_COMMENTS_COLUMN_LABELS.AUDITOR_DETAILS}</div>
            <div className={styles.AuditorCardBody}>
                {rows
                    .filter(([, value]) => !!value)
                    .map(([label, value, isName]) => (
                        <div key={label} className={styles.AuditorCardRow}>
                            <span className={styles.AuditorCardLabel}>{label}</span>
                            <span className={isName ? styles.AuditorCardName : styles.AuditorCardValue}>{value}</span>
                        </div>
                    ))}
            </div>
        </div>
    );
};

const hasAuditorExtraDetails = (person: AuditorPerson) =>
    [person.membershipNum, person.firmRegistrationNumber, person.pan, person.address].some(Boolean);
